/* The Harvest Company — Skeleton host custom element.
   Pairs with theme/assets/hc-skeleton.css and theme/snippets/hc-skel.liquid.

   Why a custom element: we want one shared lifecycle for every async surface
   on the storefront — delay before show, max-show timeout, content swap,
   accessibility announcements — without each section reinventing it.

   Usage in Liquid:

     <hc-skel-host data-delay="150" data-max-show="8000" data-label="products">
       <template data-role="skeleton">
         {% render 'hc-skel', kind: 'product-card', count: 4 %}
       </template>
       <div data-role="skeleton-slot" aria-hidden="true"></div>
       <div data-role="content"></div>
       <div data-role="empty" hidden>Nothing to show.</div>
     </hc-skel-host>

   Two ways to resolve:
     1. host.setContent(htmlString)  — replaces content and exits loading.
     2. dispatch CustomEvent('hc-skel:ready', { detail: { html } }) on host.
     3. host.setEmpty()              — explicit empty fallback.
   data-max-show in ms is a hard timeout to the empty state. */

(function () {
  if (window.customElements && window.customElements.get('hc-skel-host')) return;

  class HCSkelHost extends HTMLElement {
    constructor () {
      super();
      this._showTimer = null;
      this._maxTimer = null;
      this._announced = false;
    }

    connectedCallback () {
      if (this._connected) return;
      this._connected = true;

      // Honor reduced motion: no delay, but no shimmer either (CSS handles that).
      const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const delay = reduced ? 0 : parseInt(this.dataset.delay || '150', 10);
      const maxShow = parseInt(this.dataset.maxShow || '8000', 10);

      // Materialize the <template data-role="skeleton"> into the slot once,
      // so we don't pay the parse cost on every show/hide.
      this._hydrateSkeleton();

      // Build SR-only status node.
      this._sr = document.createElement('span');
      this._sr.className = 'hc-skel-sr';
      this._sr.setAttribute('role', 'status');
      this._sr.setAttribute('aria-live', 'polite');
      this.appendChild(this._sr);

      // Delay before showing skeleton — avoids flash on fast loads.
      this._showTimer = window.setTimeout(() => {
        if (this.dataset.state === 'ready' || this.dataset.state === 'empty') return;
        this.setAttribute('data-loading', 'true');
        this.setAttribute('aria-busy', 'true');
        const label = this.dataset.label || 'content';
        this._sr.textContent = `Loading ${label}…`;
      }, Math.max(0, delay));

      // Hard timeout to empty state.
      this._maxTimer = window.setTimeout(() => this.setEmpty(), Math.max(1000, maxShow));

      // Public event API: host content via CustomEvent.
      this.addEventListener('hc-skel:ready', (e) => {
        if (e && e.detail && typeof e.detail.html === 'string') this.setContent(e.detail.html);
        else this.setContent();
      });
    }

    disconnectedCallback () {
      this._clearTimers();
    }

    _clearTimers () {
      if (this._showTimer) { window.clearTimeout(this._showTimer); this._showTimer = null; }
      if (this._maxTimer)  { window.clearTimeout(this._maxTimer);  this._maxTimer = null; }
    }

    _hydrateSkeleton () {
      const tpl = this.querySelector(':scope > template[data-role="skeleton"]');
      const slot = this.querySelector(':scope > [data-role="skeleton-slot"]');
      if (!tpl || !slot) return;
      if (slot.dataset.hydrated === '1') return;
      slot.appendChild(tpl.content.cloneNode(true));
      slot.dataset.hydrated = '1';
    }

    /* Replace the content slot and exit loading state.
       If `html` is omitted, the existing markup inside [data-role="content"]
       is assumed to be the real content (server-rendered case where
       skeletons are only shown briefly while sub-resources load). */
    setContent (html) {
      this._clearTimers();
      const content = this.querySelector(':scope > [data-role="content"]');
      if (content && typeof html === 'string') content.innerHTML = html;
      this.removeAttribute('data-loading');
      this.setAttribute('aria-busy', 'false');
      this.dataset.state = 'ready';
      const label = this.dataset.label || 'content';
      if (!this._announced) {
        this._sr.textContent = `${label.charAt(0).toUpperCase()}${label.slice(1)} loaded.`;
        this._announced = true;
      }
      this.dispatchEvent(new CustomEvent('hc-skel:resolved', { bubbles: true }));
    }

    setEmpty () {
      this._clearTimers();
      this.removeAttribute('data-loading');
      this.setAttribute('aria-busy', 'false');
      this.dataset.state = 'empty';
      const label = this.dataset.label || 'content';
      this._sr.textContent = `No ${label} available.`;
      this.dispatchEvent(new CustomEvent('hc-skel:empty', { bubbles: true }));
    }
  }

  window.customElements.define('hc-skel-host', HCSkelHost);
})();
